<script setup lang="ts">
const props = withDefaults(defineProps<{
  label: string;
  min: number;
  max: number;
  step: number;
  sliderAriaLabel: string;
  disabled?: boolean;
}>(), {
  disabled: false,
});

const modelValue = defineModel<number>({ required: true });
const buttonIncrement = 0.05;

function changeValue(direction: number) {
  const value = modelValue.value + direction * buttonIncrement;
  modelValue.value = Math.round(Math.min(props.max, Math.max(props.min, value)) * 100) / 100;
}
</script>

<template>
  <div class="quick-settings-container">
    <div class="quick-settings-label">
      {{ label }}
    </div>
    <div class="quick-settings-control">
      <button
        type="button"
        class="quick-jump-btn"
        :disabled="disabled"
        :aria-label="`Decrease ${label} by 5%`"
        @click="changeValue(-1)"
      >
        -
      </button>
      <input
        v-model.number="modelValue"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        class="quick-slider"
        :aria-label="sliderAriaLabel"
      >
      <button
        type="button"
        class="quick-jump-btn"
        :disabled="disabled"
        :aria-label="`Increase ${label} by 5%`"
        @click="changeValue(1)"
      >
        +
      </button>
      <span class="quick-settings-value">{{ Math.round(modelValue * 100) }}%</span>
    </div>
  </div>
</template>

<style scoped>
/* Label and control share a line where there's room and stack where there isn't
   — the control's flex-basis is what decides, so the same row serves a wide
   popover and a ~300px side pane without a layout prop. */
.quick-settings-container {
  align-items: center;
  gap: 2px 12px;
  min-height: 44px;
}

.quick-settings-label {
  min-width: 84px;
  flex-shrink: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.quick-settings-control {
  flex: 1 1 220px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

/* ─── Slider ──────────────────────────────────────────────────────
   Custom thumb so the touch target is comfortable on mobile. Native
   accent-color stops applying once the pseudo-elements are styled,
   so thumb / track colors are set explicitly. text-primary keeps
   contrast in both themes (light primary token is too pale to read
   against the surface-hover track). */
.quick-slider {
  flex: 1;
  min-width: 0;
  -webkit-appearance: none;
  appearance: none;
  height: 32px;
  background: transparent;
  cursor: pointer;
  margin: 0;
}

.quick-slider::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 999px;
  background: var(--color-surface-hover);
}
.quick-slider::-moz-range-track {
  height: 6px;
  border-radius: 999px;
  background: var(--color-surface-hover);
}

.quick-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 24px;
  width: 24px;
  margin-top: -9px;
  border-radius: 999px;
  background: var(--color-text-primary);
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: transform 0.12s ease;
}
.quick-slider::-moz-range-thumb {
  height: 24px;
  width: 24px;
  border-radius: 999px;
  background: var(--color-text-primary);
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: transform 0.12s ease;
}

/* Vendor-prefixed pseudo-elements have to live in their own rules — combining
   ::-webkit-slider-thumb and ::-moz-range-thumb in one selector list
   invalidates the whole rule in both engines. */
.quick-slider:focus-visible::-webkit-slider-thumb {
  outline: 2px solid var(--color-text-primary);
  outline-offset: 3px;
}
.quick-slider:focus-visible::-moz-range-thumb {
  outline: 2px solid var(--color-text-primary);
  outline-offset: 3px;
}

.quick-slider:not(:disabled):active::-webkit-slider-thumb {
  transform: scale(1.1);
}
.quick-slider:not(:disabled):active::-moz-range-thumb {
  transform: scale(1.1);
}

.quick-slider:disabled {
  cursor: not-allowed;
}
.quick-slider:disabled::-webkit-slider-runnable-track {
  opacity: 0.5;
}
.quick-slider:disabled::-moz-range-track {
  opacity: 0.5;
}
.quick-slider:disabled::-webkit-slider-thumb {
  background: color-mix(in srgb, var(--color-text-primary) 45%, var(--color-surface-hover));
  cursor: not-allowed;
  box-shadow: none;
}
.quick-slider:disabled::-moz-range-thumb {
  background: color-mix(in srgb, var(--color-text-primary) 45%, var(--color-surface-hover));
  cursor: not-allowed;
  box-shadow: none;
}

.quick-settings-value {
  flex: 0 0 auto;
  min-width: 44px;
  text-align: right;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.quick-jump-btn {
  flex: 0 0 auto;
  min-width: 44px;
  min-height: 44px;
  padding: 0 10px;
  border: 1px solid var(--color-surface-hover);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.1s ease, border-color 0.15s ease;
}

.quick-jump-btn:hover:not(:disabled) {
  background: var(--color-surface-hover);
  border-color: color-mix(in srgb, var(--color-text-primary) 25%, var(--color-surface-hover));
}

.quick-jump-btn:active:not(:disabled) {
  transform: scale(0.96);
}

.quick-jump-btn:focus-visible {
  outline: 2px solid var(--color-text-primary);
  outline-offset: 2px;
}

.quick-jump-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
